
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import AuthForm from '@/components/AuthForm';
import AudioAnalyzer from '@/components/AudioAnalyzer';
import { AuroraBackground } from '@/components/ui/aurora-background';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import NavBar from '@/components/NavBar';

const Index = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuth();
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        return;
      }
      
      if (session?.user) {
        if (!session.user.email_confirmed_at) {
          setIsAuthenticated(false);
          toast.error('Please verify your email before accessing the analyzer');
          return;
        }
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkAuth = async () => {
    try {
      console.log('Checking auth status...');
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
        return;
      }

      if (session?.user && session.user.email_confirmed_at) {
        console.log('User is authenticated');
        setIsAuthenticated(true);
      } else {
        console.log('User is not authenticated');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
    }
  };

  const handleSignOut = async () => {
    console.log('Signing out...');
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toast.error('Error signing out');
      } else {
        toast.success('Signed out successfully');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Error signing out');
    }
  };

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden">
      {!isAuthenticated ? (
        <AuroraBackground>
          <div className="container mx-auto px-4 pt-20 pb-32">
            <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 dark:text-white">
                Analyze calls
                <br className="hidden md:block" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-violet-600">
                  with AI precision
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Premium AI-powered call analysis built with cutting-edge technology.
                Save time and improve performance with instant, actionable feedback.
              </p>
              <AuthForm onAuthSuccess={() => setIsAuthenticated(true)} />
            </div>
          </div>
        </AuroraBackground>
      ) : (
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <div className="flex-1 relative bg-gray-50">
              <NavBar onSignOut={handleSignOut} isAuthenticated={isAuthenticated} />
              <div className="p-8">
                <AudioAnalyzer onSignOut={handleSignOut} />
              </div>
            </div>
          </div>
        </SidebarProvider>
      )}
    </div>
  );
};

export default Index;
